import { jsx, jsxs } from "react/jsx-runtime";
import { Button, DatePicker, Heading, PlainButton, TRANSFORMER_MAX_MOBILE_WIDTH_PX, TravelerSelection } from "@pcln/horizon";
import { addDays } from "date-fns/addDays";
import { addMonths } from "date-fns/addMonths";
import { format } from "date-fns/format";
import { parse } from "date-fns/parse";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useMediaQuery } from "react-responsive";
import { FormAutocompleteField, resolveAutocompleteFieldOptions } from "../shared/components/Autocomplete/FormAutocompleteField.js";
import { SubmitButton } from "../shared/components/SubmitButton.js";
import { DEFAULT_COLLISION_AVOIDANCE } from "../shared/constants.js";
import { useTabGroup } from "../shared/hooks/useTabGroup.js";
import { createYupResolver } from "../shared/validation/yup-form-resolver.js";
import { addMultiHotelDestination, getMultiHotelInitialValues, getMultiHotelTravelerState, removeMultiHotelDestination, setMultiHotels } from "./MultiHotelSearchForm.utils.js";
import { getMultiHotelFormContainerClass, getMultiHotelGridContainerClass, getMultiHotelInputClass } from "./MultiHotelSearchForm.variants.js";
import { MAXIMUM_MULTI_HOTEL_GUEST_COUNT, multiHotelSearchFormSchema } from "./validation.js";
function useMultiHotelFormValues({ clearErrors, control, initialValues, onFormValuesChange, trigger, watch }) {
    const watchHotels = useWatch({
        control,
        name: 'hotels',
        defaultValue: initialValues.hotels
    });
    const watchTravelers = useWatch({
        control,
        name: 'travelers',
        defaultValue: initialValues.travelers
    });
    const previousHotelsRef = useRef(watchHotels);
    useEffect(()=>{
        const subscription = watch((value)=>{
            onFormValuesChange?.(value);
        });
        return ()=>subscription.unsubscribe();
    }, [
        watch,
        onFormValuesChange
    ]);
    useEffect(()=>{
        watchHotels.forEach((hotel, index)=>{
            const previousHotel = previousHotelsRef.current[index];
            if (!previousHotel || previousHotel.id !== hotel.id) return void clearErrors(`hotels.${index}`);
            if (previousHotel.endLocation !== hotel.endLocation) clearErrors(`hotels.${index}.endLocation`);
            if (previousHotel.startDate !== hotel.startDate) clearErrors(`hotels.${index}.startDate`);
            if (previousHotel.endDate !== hotel.endDate) clearErrors(`hotels.${index}.endDate`);
        });
        previousHotelsRef.current = watchHotels;
    }, [
        clearErrors,
        watchHotels
    ]);
    useEffect(()=>{
        trigger('travelers.children');
    }, [
        trigger,
        watchTravelers
    ]);
    return {
        watchHotels,
        watchTravelers
    };
}
function MultiHotelSearchForm({ onSubmit, initialData, initialValues, isMobile = false, renderAutocomplete, autocompleteOptions, onFormValuesChange, autoAdvance = false, typeAheadEndpoint = 'hotels', collisionAvoidance = DEFAULT_COLLISION_AVOIDANCE, submitLabel = 'Find Your Hotels', submitButtonType = 'primary', submitButtonEmphasis }) {
    const mergedInitialValues = useMemo(()=>{
        const baseValues = getMultiHotelInitialValues(initialData);
        return {
            ...baseValues,
            ...initialValues,
            travelers: getMultiHotelTravelerState({
                ...baseValues.travelers,
                ...initialValues?.travelers
            })
        };
    }, [
        initialData,
        initialValues
    ]);
    const { registerTabElement, focusNextElement } = useTabGroup();
    const [activeHotelState, setActiveHotelState] = useState({});
    const dateFieldRefs = useRef({});
    const { clearErrors, control, setValue, handleSubmit, watch, trigger, formState: { errors } } = useForm({
        defaultValues: mergedInitialValues,
        mode: 'onSubmit',
        resolver: createYupResolver(multiHotelSearchFormSchema),
        reValidateMode: 'onChange'
    });
    const isMobileComponent = useMediaQuery({
        maxWidth: TRANSFORMER_MAX_MOBILE_WIDTH_PX
    });
    const { watchHotels } = useMultiHotelFormValues({
        clearErrors,
        control,
        initialValues: mergedInitialValues,
        onFormValuesChange,
        trigger,
        watch
    });
    const onCalendarChange = useCallback((dates, index)=>{
        const hotel = watchHotels[index];
        if (!hotel) return;
        const hotelId = hotel.id;
        if (dates?.from) if (dates?.to && dates.to.getTime() !== dates.from.getTime()) {
            const updates = {
                startDate: format(dates.from, 'yyyyMMdd'),
                endDate: format(dates.to, 'yyyyMMdd')
            };
            const result = setMultiHotels(watchHotels, updates, index);
            setValue('hotels', result, {
                shouldDirty: true
            });
            setActiveHotelState((prev)=>({
                    ...prev,
                    [hotelId]: {}
                }));
            if (autoAdvance && !isMobile) focusNextElement(false, dateFieldRefs.current[hotelId] ?? void 0);
        } else setActiveHotelState((prev)=>({
                ...prev,
                [hotelId]: {
                    tempStartDate: dates.from
                }
            }));
    }, [
        autoAdvance,
        focusNextElement,
        isMobile,
        setValue,
        watchHotels
    ]);
    const currentDate = useMemo(()=>new Date(), []);
    const getSelectedDates = useCallback((hotel)=>{
        const hotelState = activeHotelState[hotel.id];
        const fromDate = hotelState?.tempStartDate || (hotel.startDate ? parse(hotel.startDate, 'yyyyMMdd', new Date()) : void 0);
        const toDate = hotel.endDate && hotel.endDate !== hotel.startDate ? parse(hotel.endDate, 'yyyyMMdd', new Date()) : void 0;
        return {
            from: fromDate,
            to: toDate
        };
    }, [
        activeHotelState
    ]);
    const onItemSelect = useCallback((item, index)=>{
        const result = setMultiHotels(watchHotels, {
            endLocation: item
        }, index);
        setValue('hotels', result, {
            shouldDirty: true
        });
        if (!isMobile && item && autoAdvance) focusNextElement(true);
        else if (isMobile && !!item && document.activeElement instanceof HTMLElement) document.activeElement.blur();
    }, [
        autoAdvance,
        focusNextElement,
        isMobile,
        setValue,
        watchHotels
    ]);
    const handleAddHotel = useCallback(()=>{
        const result = addMultiHotelDestination(watchHotels);
        if (result) setValue('hotels', result, {
            shouldDirty: true
        });
    }, [
        setValue,
        watchHotels
    ]);
    const handleRemoveHotel = useCallback((index)=>{
        const result = removeMultiHotelDestination(watchHotels, index);
        setValue('hotels', result, {
            shouldDirty: true
        });
        clearErrors(`hotels.${index}`);
    }, [
        clearErrors,
        setValue,
        watchHotels
    ]);
    const handleTravelerChange = useCallback((travelerInfo)=>{
        setValue('travelers', getMultiHotelTravelerState(travelerInfo));
    }, [
        setValue
    ]);
    const handleValidSubmit = useCallback((data)=>{
        onSubmit({
            ...data,
            travelers: getMultiHotelTravelerState(data.travelers)
        });
    }, [
        onSubmit
    ]);
    const handleSubmitHelper = useMemo(()=>handleSubmit(handleValidSubmit), [
        handleSubmit,
        handleValidSubmit
    ]);
    const getMaxEndDate = useCallback((hotelId)=>{
        const hotelState = activeHotelState[hotelId];
        return hotelState?.tempStartDate ? addDays(hotelState.tempStartDate, 28) : void 0;
    }, [
        activeHotelState
    ]);
    return /*#__PURE__*/ jsx("form", {
        className: "w-auto",
        "aria-label": "horizon-multi-hotel-search-form",
        onSubmit: handleSubmitHelper,
        children: /*#__PURE__*/ jsx("div", {
            className: getMultiHotelFormContainerClass(),
            children: /*#__PURE__*/ jsxs("div", {
                className: getMultiHotelGridContainerClass(),
                children: [
                    /*#__PURE__*/ jsx("div", {
                        className: getMultiHotelInputClass('repeatFormControls'),
                        children: /*#__PURE__*/ jsx("div", {
                            className: getMultiHotelInputClass('hotelMulti'),
                            children: watchHotels.map((hotel, index)=>/*#__PURE__*/ jsxs("div", {
                                    className: getMultiHotelInputClass('hotelRow'),
                                    children: [
                                        /*#__PURE__*/ jsxs("div", {
                                            className: getMultiHotelInputClass('hotelHeader'),
                                            children: [
                                                /*#__PURE__*/ jsxs(Heading, {
                                                    as: "h6",
                                                    children: [
                                                        "Hotel ",
                                                        index + 1
                                                    ]
                                                }),
                                                index > 0 && /*#__PURE__*/ jsx(PlainButton, {
                                                    type: "primary",
                                                    textStyle: "body2",
                                                    buttonType: "button",
                                                    slots: {
                                                        textSlot: 'no-underline hover:underline'
                                                    },
                                                    onClick: ()=>handleRemoveHotel(index),
                                                    children: "Remove"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ jsxs("div", {
                                            className: getMultiHotelInputClass('hotelInputsGrid'),
                                            children: [
                                                /*#__PURE__*/ jsx("div", {
                                                    className: "w-full",
                                                    children: /*#__PURE__*/ jsx(FormAutocompleteField, {
                                                        ...resolveAutocompleteFieldOptions(autocompleteOptions, {
                                                            searchKey: `hotels.${index}.endLocation`,
                                                            typeAheadEndpoint: typeAheadEndpoint
                                                        }),
                                                        renderAutocomplete: renderAutocomplete,
                                                        label: "Where to?",
                                                        typeAheadEndpoint: typeAheadEndpoint,
                                                        errorMessage: errors?.hotels?.[index]?.endLocation?.message,
                                                        searchKey: `hotels.${index}.endLocation`,
                                                        initialSelectedItem: hotel.endLocation ?? void 0,
                                                        slotExtras: {
                                                            hotelIndex: index
                                                        },
                                                        onItemSelect: (itemSelected)=>{
                                                            onItemSelect(itemSelected, index);
                                                        },
                                                        inputRef: autoAdvance ? registerTabElement : void 0,
                                                        collisionAvoidance: collisionAvoidance
                                                    })
                                                }),
                                                /*#__PURE__*/ jsx("div", {
                                                    className: getMultiHotelInputClass('datePicker'),
                                                    children: /*#__PURE__*/ jsx(DatePicker, {
                                                        className: "w-full",
                                                        title: "Hotels: Check-in - Check-out",
                                                        label: "Check-in - Check-out",
                                                        mode: "range",
                                                        numberOfMonths: isMobileComponent ? 13 : 2,
                                                        defaultMonth: hotel.startDate ? parse(hotel.startDate, 'yyyyMMdd', new Date()) : void 0,
                                                        handleCalendarChange: (dates)=>{
                                                            onCalendarChange(dates, index);
                                                        },
                                                        errorMessage: errors?.hotels?.[index]?.startDate?.message || errors?.hotels?.[index]?.endDate?.message,
                                                        inputRef: autoAdvance ? (elem)=>{
                                                            registerTabElement(elem);
                                                            dateFieldRefs.current[hotel.id] = elem;
                                                        } : void 0,
                                                        selected: getSelectedDates(hotel),
                                                        disabled: {
                                                            before: currentDate,
                                                            after: getMaxEndDate(hotel.id)
                                                        },
                                                        startMonth: currentDate,
                                                        endMonth: addMonths(currentDate, 12),
                                                        min: 1,
                                                        collisionAvoidance: collisionAvoidance
                                                    }, `datepicker-${hotel.id}`)
                                                })
                                            ]
                                        })
                                    ]
                                }, `hotel-${hotel.id}`))
                        })
                    }),
                    /*#__PURE__*/ jsx(Button, {
                        buttonType: "button",
                        size: "sm",
                        className: getMultiHotelInputClass('addHotelButton'),
                        onClick: handleAddHotel,
                        disabled: watchHotels.length > 4,
                        children: "Add Another Hotel"
                    }),
                    /*#__PURE__*/ jsxs("div", {
                        className: getMultiHotelInputClass('travelersSubmitContainer'),
                        children: [
                            /*#__PURE__*/ jsx("div", {
                                className: getMultiHotelInputClass('travelersWrapper'),
                                children: /*#__PURE__*/ jsx(TravelerSelection, {
                                    isHotel: true,
                                    initialValues: mergedInitialValues.travelers,
                                    roomsEnabled: true,
                                    maxRooms: 9,
                                    maxChildren: 8,
                                    maxTravelers: MAXIMUM_MULTI_HOTEL_GUEST_COUNT,
                                    ageSelectEnabled: true,
                                    tooltipErrorMessageId: "travelerSelectionError",
                                    errorMessage: errors?.travelers?.children?.message || errors?.travelers?.rooms?.message || '',
                                    onChange: handleTravelerChange,
                                    collisionAvoidance: collisionAvoidance
                                })
                            }),
                            /*#__PURE__*/ jsx(SubmitButton, {
                                className: getMultiHotelInputClass('submit'),
                                isMobile: isMobile,
                                type: submitButtonType,
                                emphasis: submitButtonEmphasis,
                                children: submitLabel
                            })
                        ]
                    })
                ]
            })
        })
    });
}
export { MultiHotelSearchForm };
